import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, X, ArrowUpRight, Sparkles } from "lucide-react";

/**
 * Anime Music Video gallery — conversion-driver for addon_anime_video.
 * Sources are served from /app/frontend/public/assets so they work in
 * any browser (including headless Chromium that lacks proprietary H.264).
 * Add new reels by dropping new files in /app/frontend/public/assets and
 * appending to the `tiles` array.
 */

const SAMPLE_SOURCES = [
  { src: "/assets/anime-sample.webm", type: "video/webm" },
  { src: "/assets/anime-sample.mp4", type: "video/mp4" },
];

const QUANTUM_NOIR_SOURCES = [
  { src: "/assets/quantum-noir.webm", type: "video/webm" },
  { src: "/assets/quantum-noir.mp4", type: "video/mp4" },
];

const INK_SHADOW_SOURCES = [
  { src: "/assets/ink-shadow.webm", type: "video/webm" },
  { src: "/assets/ink-shadow.mp4", type: "video/mp4" },
];

const LUCID_SOURCES = [
  { src: "/assets/lucid.webm", type: "video/webm" },
  { src: "/assets/lucid.mp4", type: "video/mp4" },
];

const ICON_SOURCES = [
  { src: "/assets/icon.webm", type: "video/webm" },
  { src: "/assets/icon.mp4", type: "video/mp4" },
];

const ASCEND_SOURCES = [
  { src: "/assets/ascend.webm", type: "video/webm" },
  { src: "/assets/ascend.mp4", type: "video/mp4" },
];

const tiles = [
  {
    id: "signature",
    sources: SAMPLE_SOURCES,
    title: "SELECT YOUR",
    headline: "QUANTUM DISCIPLINE",
    subtitle: "Five realities. One artist. Infinite perception.",
    tag: "THE GALLERY",
    featured: true,
    hero: true,
    accent: "linear-gradient(135deg,#d4af37 0%,#1a1a1d 100%)",
  },
  {
    id: "quantum_noir",
    sources: QUANTUM_NOIR_SOURCES,
    title: "THE ANOMALY",
    subtitle: "Shadow, gold, and slow-burn cinematic noir.",
    tag: "DISCIPLINE // 001",
    accent: "linear-gradient(135deg,#1a1a1d 0%,#d4af37 100%)",
  },
  {
    id: "ink",
    sources: INK_SHADOW_SOURCES,
    title: "THE ARCHITECT",
    subtitle: "High-contrast cel work · sumi-e brush energy.",
    tag: "DISCIPLINE // 002",
    accent: "linear-gradient(135deg,#ffffff 0%,#1a1a1d 100%)",
  },
  {
    id: "vapor",
    sources: LUCID_SOURCES,
    title: "LUCID",
    subtitle: "Pastel skies · sunset cities · slow-motion melancholy.",
    tag: "DISCIPLINE // 003",
    accent: "linear-gradient(135deg,#ff6ec4 0%,#7873f5 100%)",
  },
  {
    id: "shonen",
    sources: ICON_SOURCES,
    title: "ICON",
    subtitle: "Speed lines · energy bursts · championship arc.",
    tag: "DISCIPLINE // 004",
    accent: "linear-gradient(135deg,#ff5b3b 0%,#ffd60a 100%)",
  },
  {
    id: "editorial",
    sources: ASCEND_SOURCES,
    title: "ASCEND",
    subtitle: "Magazine-cover aesthetic · cinematic typography.",
    tag: "DISCIPLINE // 005",
    accent: "linear-gradient(135deg,#0a0a0a 0%,#d4af37 100%)",
  },
];

function Tile({ tile, onOpen }) {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [playable, setPlayable] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onCanPlay = () => setPlayable(true);
    const onErr = () => setPlayable(false);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("error", onErr);
    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("error", onErr);
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (tile.featured || hovered) {
      v.play().catch(() => {});
    } else {
      try {
        v.pause();
        v.currentTime = 0;
      } catch (e) {
        // ignore
      }
    }
  }, [hovered, tile.featured]);

  return (
    <div
      data-testid={`anime-tile-${tile.id}`}
      className={
        "relative group overflow-hidden border border-white/10 bg-[#0a0a0a] card-hover " +
        (tile.hero ? "" : "cursor-pointer ") +
        (tile.featured ? "lg:row-span-2 lg:col-span-2" : "")
      }
      style={{ aspectRatio: tile.featured ? "1 / 1" : "4 / 5" }}
      onClick={() => {
        if (!tile.hero) onOpen(tile);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Gradient backdrop (visible until video paints) */}
      <div
        className="absolute inset-0"
        style={{ background: tile.accent }}
      />
      <div className="absolute inset-0 noise" />

      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload={tile.featured ? "auto" : "metadata"}
        autoPlay={tile.featured}
        className={
          "absolute inset-0 w-full h-full object-cover transition-all duration-700 " +
          (playable
            ? (tile.hero ? "opacity-30" : "opacity-100 group-hover:scale-[1.04]")
            : "opacity-0")
        }
      >
        {tile.sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </video>

      <div
        className={
          "absolute inset-0 " +
          (tile.hero
            ? "bg-gradient-to-br from-black/80 via-black/60 to-black/85"
            : "bg-gradient-to-t from-black via-black/40 to-transparent")
        }
      />

      {/* Top corner tag */}
      <div className="absolute top-4 left-4 z-10">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 bg-black/60 text-[var(--primary)] border border-[var(--primary)]/30 backdrop-blur">
          {tile.tag}
        </span>
      </div>

      {/* Play icon (suppressed for hero) */}
      {!tile.hero && (
        <div className="absolute top-4 right-4 z-10 w-9 h-9 border border-white/20 flex items-center justify-center backdrop-blur-md bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <Play size={14} className="ml-0.5" strokeWidth={1.5} />
        </div>
      )}

      {/* Hero layout — centered typographic panel */}
      {tile.hero ? (
        <div className="absolute inset-0 z-10 flex flex-col items-start justify-center p-5 sm:p-8 md:p-12">
          <div className="font-mono text-[9px] sm:text-[10px] md:text-xs tracking-[0.28em] uppercase text-[var(--primary)] mb-3 sm:mb-5">
            {tile.title}
          </div>
          <h3 className="font-heading font-black tracking-tighter text-white text-2xl sm:text-4xl md:text-6xl lg:text-7xl leading-[0.92] break-words">
            {tile.headline}
          </h3>
          <div className="h-px w-12 sm:w-16 bg-[var(--primary)] my-3 sm:my-5 md:my-7" />
          <p className="text-zinc-200 text-xs sm:text-sm md:text-base leading-relaxed max-w-sm whitespace-pre-line">
            {tile.subtitle.split(". ").filter(Boolean).map((s, i) => (
              <span key={`${tile.id}-line-${i}-${s.slice(0, 8)}`} className="block">
                {s.endsWith(".") ? s : s + "."}
              </span>
            ))}
          </p>
        </div>
      ) : (
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 z-10">
          <h3 className="font-heading font-black tracking-tighter text-white text-xl md:text-2xl">
            {tile.title}
          </h3>
          <p className="text-zinc-300 text-xs md:text-sm mt-2 leading-relaxed max-w-md">
            {tile.subtitle}
          </p>
        </div>
      )}
    </div>
  );
}

function VideoLightbox({ tile, onClose }) {
  if (!tile) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-10"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="anime-lightbox"
    >
      <div className="relative w-full max-w-5xl">
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-zinc-400 hover:text-white"
          data-testid="anime-lightbox-close"
        >
          <X size={22} />
        </button>
        <div className="border border-white/10 bg-[#020202]">
          <video
            controls
            autoPlay
            playsInline
            className="w-full h-auto max-h-[85vh] object-contain bg-black"
          >
            {tile.sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </video>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--primary)]">
              {tile.tag} · SIGNATURE PERFORMANCE
            </div>
            <h3 className="font-heading font-black text-2xl md:text-3xl tracking-tighter text-white mt-1">
              {tile.title}
            </h3>
            <p className="text-zinc-400 text-sm mt-1">{tile.subtitle}</p>
          </div>
          <Link to="/pricing" className="btn-primary">
            Get this for your track <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AnimeVideoGallery() {
  const [open, setOpen] = useState(null);

  return (
    <section className="relative py-24 md:py-32 border-y border-white/10 bg-[#070707] overflow-hidden">
      <div className="absolute inset-0 dotted-grid opacity-20 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-7">
            <div className="tag mb-4">
              <Sparkles size={12} /> Signature Performance Video · The Gallery
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Choose the world
              <br />
              <span className="text-[var(--primary)]">your music</span>
              <br />
              lives in.
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-zinc-400 text-base leading-relaxed">
              Five quantum disciplines. One artist. Pick a reality — we build
              the world your song lives in. Two rounds of revisions included.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-fr">
          {tiles.map((t) => (
            <Tile key={t.id} tile={t} onOpen={setOpen} />
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-12 p-6 md:p-8 border border-[var(--primary)]/30 bg-[var(--primary)]/5 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2">
              Signature Performance Video · Add-on
            </div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">
              One track. One video. Yours forever.
            </h3>
            <p className="text-zinc-400 text-sm mt-2 max-w-lg">
              Available as an add-on to any tier. See pricing for details.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="anime-cta-pricing"
            className="btn-primary"
          >
            See pricing <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>

      <VideoLightbox tile={open} onClose={() => setOpen(null)} />
    </section>
  );
}
